import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { useLabTests, useCreateLabTest, useUpdateLabTest, useDeleteLabTest } from '../../hooks/useLabTests';
import { PageHeader } from '../../components/common/PageHeader';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { LabTestForm } from '../../components/labTests/LabTestForm';
import { Table } from '../../components/common/Table';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { LabTest } from '../../types/api.types';
import { CreateLabTestDto, UpdateLabTestDto } from '../../api/labTests.api';
import toast from 'react-hot-toast';

export const LabTestsPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLabTest, setEditingLabTest] = useState<LabTest | null>(null);
  const [deleteLabTestId, setDeleteLabTestId] = useState<number | null>(null);

  const { data: labTests, isLoading, error } = useLabTests();
  const createMutation = useCreateLabTest();
  const updateMutation = useUpdateLabTest();
  const deleteMutation = useDeleteLabTest();

  const handleCreate = async (data: CreateLabTestDto) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success(t('labTests.labTestCreated'));
      setIsModalOpen(false);
    } catch (error) {
      toast.error(t('labTests.labTestCreatedError'));
    }
  };

  const handleUpdate = async (data: CreateLabTestDto | UpdateLabTestDto) => {
    if (!editingLabTest) return;
    const updateData = data as UpdateLabTestDto;

    try {
      await updateMutation.mutateAsync({ id: editingLabTest.id, data: updateData });
      toast.success(t('labTests.labTestUpdated'));
      setIsModalOpen(false);
      setEditingLabTest(null);
    } catch (error) {
      toast.error(t('labTests.labTestUpdatedError'));
    }
  };

  const handleDelete = async () => {
    if (!deleteLabTestId) return;
    
    try {
      await deleteMutation.mutateAsync(deleteLabTestId);
      toast.success(t('labTests.labTestDeleted'));
      setDeleteLabTestId(null);
    } catch (error) {
      toast.error(t('labTests.labTestDeletedError'));
    }
  };

  const handleEdit = (labTest: LabTest) => {
    setEditingLabTest(labTest);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLabTest(null);
  };

  return (
    <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        title={t('labTests.title')}
        actions={
          <Button
            variant="primary"
            onClick={() => setIsModalOpen(true)}
          >
            {isRTL ? (
              <>
                <Plus className="ms-2" size={16} />
                {t('labTests.addLabTest')}
              </>
            ) : (
              <>
                <Plus className="me-2" size={16} />
                {t('labTests.addLabTest')}
              </>
            )}
          </Button>
        }
      />

      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {error ? (
                <div className="p-4">
                  <ErrorMessage message={error.message || t('errors.required')} />
                </div>
              ) : (
                <Table
                  data={Array.isArray(labTests) ? labTests : []}
                  columns={[
                    {
                      key: 'name',
                      header: t('labTests.name'),
                      render: (labTest) => (
                        <span className="fw-semibold text-dark">{labTest.name}</span>
                      ),
                      sortable: true,
                      width: '40%',
                    },
                    {
                      key: 'description',
                      header: t('labTests.description'),
                      render: (labTest) => (
                        <span className="text-muted">{labTest.description || '-'}</span>
                      ),
                      sortable: true,
                      width: '45%',
                    },
                    {
                      key: 'actions',
                      header: t('common.actions'),
                      render: (labTest) => (
                        <div className={`d-flex gap-2 ${isRTL ? 'justify-content-start' : 'justify-content-end'}`}>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleEdit(labTest);
                            }}
                            className="p-2"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              setDeleteLabTestId(labTest.id);
                            }}
                            className="p-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ),
                      width: '15%',
                    },
                  ]}
                  keyExtractor={(labTest) => labTest.id}
                  emptyMessage={t('labTests.noLabTests')}
                  pageSize={15}
                  isLoading={isLoading}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingLabTest ? t('labTests.editLabTest') : t('labTests.addLabTest')}
      >
        <LabTestForm
          labTest={editingLabTest || undefined}
          onSubmit={editingLabTest ? handleUpdate : handleCreate}
          isLoading={createMutation.isPending || updateMutation.isPending}
          onCancel={handleCloseModal}
        />
      </Modal>

      <ConfirmDialog
        isOpen={deleteLabTestId !== null}
        onClose={() => setDeleteLabTestId(null)}
        onConfirm={handleDelete}
        title={t('common.delete')}
        message={t('labTests.deleteConfirm')}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};





